/**
 * TickManager - Manages the game's idle loop.
 *
 * Handles:
 * - Regular tick intervals with accurate delta time (throttled to configured rate)
 * - Tab visibility (pause when hidden, catch up when visible)
 * - Browser sleep detection (long frames processed as offline time)
 * - Offline progress calculation in batches
 * - Error handling to prevent loop crashes
 * - Debounced persistence of last tick time
 * - HMR protection against double-starting
 * - beforeunload persistence
 */

import * as Sentry from '@sentry/react';

import { LocalStorageService } from '../services/LocalStorageService';

/**
 * Configuration options for TickManager.
 */
export interface TickManagerOptions {
  /** Target tick interval in milliseconds (default: 100 = 10 ticks/sec) */
  tickInterval?: number;

  /** Maximum time to process in one batch in milliseconds (default: 1000) */
  maxBatchTime?: number;

  /** Maximum offline time to process in milliseconds (default: 4 hours) */
  maxOfflineTime?: number;

  /** How often to persist last tick time in milliseconds (default: 5000) */
  persistInterval?: number;

  /**
   * Threshold for detecting browser sleep in milliseconds (default: 5000).
   * If a frame takes longer than this, it's treated as offline time.
   */
  sleepThreshold?: number;

  /** Callback invoked on each tick with delta time in milliseconds */
  onTick: (deltaMilliseconds: number) => void;

  /**
   * Callback invoked after offline processing.
   * @param deltaMilliseconds - Total offline time processed
   * @returns Whether to show the offline progress dialog
   */
  onOfflineProgress?: (deltaMilliseconds: number) => boolean;
}

/**
 * Result of offline progress processing.
 */
export interface OfflineProgressResult {
  /** Total time that was offline in milliseconds */
  totalOfflineTime: number;
  /** Time actually processed (may be capped) in milliseconds */
  processedTime: number;
  /** Whether the time was capped */
  wasCapped: boolean;
  /** Whether to show the dialog */
  showDialog: boolean;
}

/**
 * TickManager drives the game's idle loop.
 *
 * @example
 * ```ts
 * const tickManager = new TickManager({
 *   tickInterval: 100,
 *   onTick: (deltaMs) => {
 *     systems.tick(context, deltaMs);
 *   },
 *   onOfflineProgress: (deltaMs) => deltaMs > 60000,
 * });
 *
 * tickManager.start();
 * // ... later
 * tickManager.stop();
 * tickManager.dispose();
 * ```
 */
export class TickManager {
  // =========================================
  // Configuration (readonly after construction)
  // =========================================

  /** Target tick interval in milliseconds */
  private readonly tickInterval: number;

  /** Maximum time to process in one batch */
  private readonly maxBatchTime: number;

  /** Maximum offline time to process */
  private readonly maxOfflineTime: number;

  /** How often to persist last tick time */
  private readonly persistInterval: number;

  /** Threshold for detecting browser sleep */
  private readonly sleepThreshold: number;

  // =========================================
  // State
  // =========================================

  /** Last tick timestamp (performance.now()) */
  private lastTickTime = 0;

  /** Last time we persisted to localStorage */
  private lastPersistTime = 0;

  /** Accumulated time for throttling */
  private accumulatedTime = 0;

  /** Whether the tick loop is currently running */
  private isRunning = false;

  /** Whether ticking is paused (tab hidden) */
  private isPaused = false;

  /** Current character ID for per-character tracking */
  private currentCharacterId: string | null = null;

  /** Last offline progress result (for display) */
  private lastOfflineResult: OfflineProgressResult | null = null;

  // =========================================
  // Scheduling handles
  // =========================================

  /** requestAnimationFrame handle */
  private frameId: number | null = null;

  /** setTimeout handle for background ticking */
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  // =========================================
  // Callbacks
  // =========================================

  /** Tick callback */
  private readonly onTick: (deltaMilliseconds: number) => void;

  /** Offline progress callback */
  private readonly onOfflineProgress: ((deltaMilliseconds: number) => boolean) | undefined;

  // =========================================
  // Bound Methods (for use as callbacks)
  // =========================================

  /** Bound tick method for use in callbacks */
  private boundTick: () => void;

  /** Bound visibility change handler for use in event listeners */
  private boundHandleVisibilityChange: () => void;

  /** Bound beforeunload handler */
  private boundHandleBeforeUnload: () => void;

  // =========================================
  // Constructor
  // =========================================

  constructor(options: TickManagerOptions) {
    this.tickInterval = options.tickInterval ?? 100;
    this.maxBatchTime = options.maxBatchTime ?? 1000;
    this.maxOfflineTime = options.maxOfflineTime ?? 4 * 60 * 60 * 1000; // 4 hours
    this.persistInterval = options.persistInterval ?? 5000;
    this.sleepThreshold = options.sleepThreshold ?? 5000; // 5 seconds
    this.onTick = options.onTick;
    this.onOfflineProgress = options.onOfflineProgress;

    // Bind methods to preserve 'this' context
    this.boundTick = this.tick.bind(this);
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this);

    // Set up event listeners
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.boundHandleBeforeUnload);
    }
  }

  // =========================================
  // Public Methods
  // =========================================

  /**
   * Start the tick loop for a specific character.
   *
   * Does nothing if already running (HMR protection).
   *
   * @param characterId - The character ID to track offline progress for
   */
  start(characterId?: string): void {
    if (this.isRunning) {
      return;
    }

    this.currentCharacterId = characterId ?? null;
    this.isRunning = true;
    this.isPaused = typeof document !== 'undefined' && document.hidden;
    this.lastTickTime = performance.now();
    this.lastPersistTime = Date.now();
    this.accumulatedTime = 0;
    this.lastOfflineResult = null;

    this.scheduleNextTick();
  }

  /**
   * Stop the tick loop.
   *
   * Persists the current time to localStorage.
   */
  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    this.cancelScheduledTick();

    // Persist final tick time
    this.persistTime(true);
  }

  /**
   * Check for and process offline time for the given character.
   *
   * Call this before starting the tick loop for a character.
   *
   * @param characterId - The character ID to check offline time for
   * @returns The offline progress result, or null if no significant offline time
   */
  checkOfflineTime(characterId: string): OfflineProgressResult | null {
    const lastTickTime = LocalStorageService.getLastTickTime(characterId);
    if (lastTickTime === null) {
      return null;
    }

    const offlineTime = Date.now() - lastTickTime;

    // Only process if more than 1 second has passed
    if (offlineTime <= 1000) {
      return null;
    }

    return this.processOfflineTime(offlineTime);
  }

  /**
   * Process offline time in batches.
   *
   * @param durationMilliseconds - Total offline time in milliseconds
   * @returns The offline progress result
   */
  processOfflineTime(durationMilliseconds: number): OfflineProgressResult {
    if (durationMilliseconds <= 0) {
      return {
        totalOfflineTime: 0,
        processedTime: 0,
        wasCapped: false,
        showDialog: false,
      };
    }

    // Cap to maximum offline time
    const wasCapped = durationMilliseconds > this.maxOfflineTime;
    const cappedDuration = Math.min(durationMilliseconds, this.maxOfflineTime);

    // Process in batches to avoid freezing
    let remaining = cappedDuration;
    while (remaining > 0) {
      const batchTime = Math.min(remaining, this.maxBatchTime);
      this.safeInvokeTick(batchTime);
      remaining -= batchTime;
    }

    // Determine if we should show dialog
    const showDialog = this.onOfflineProgress?.(cappedDuration) ?? cappedDuration > 60000;

    const result: OfflineProgressResult = {
      totalOfflineTime: durationMilliseconds,
      processedTime: cappedDuration,
      wasCapped,
      showDialog,
    };

    this.lastOfflineResult = result;
    return result;
  }

  /**
   * Get the last offline progress result.
   */
  getLastOfflineResult(): OfflineProgressResult | null {
    return this.lastOfflineResult;
  }

  /**
   * Clear the last offline progress result.
   */
  clearLastOfflineResult(): void {
    this.lastOfflineResult = null;
  }

  /**
   * Clean up all resources.
   *
   * Call this when disposing the tick manager.
   */
  dispose(): void {
    this.stop();

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.boundHandleBeforeUnload);
    }
  }

  /**
   * Whether the tick loop is currently running.
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Whether ticking is paused (tab hidden).
   */
  get paused(): boolean {
    return this.isPaused;
  }

  /**
   * The current character ID being tracked.
   */
  get characterId(): string | null {
    return this.currentCharacterId;
  }

  // =========================================
  // Private Methods
  // =========================================

  /**
   * Main tick function with throttling.
   *
   * Uses RAF for timing but only processes at the configured tick rate.
   * Detects browser sleep via long frame times and processes as offline.
   */
  private tick(): void {
    if (!this.isRunning) {
      return;
    }

    const now = performance.now();
    let deltaMilliseconds = now - this.lastTickTime;
    this.lastTickTime = now;

    // Handle edge cases
    if (deltaMilliseconds < 0) {
      // Clock went backwards (rare but possible)
      deltaMilliseconds = this.tickInterval;
    }

    // Check for browser sleep - if frame took way too long, treat as offline
    if (deltaMilliseconds > this.sleepThreshold) {
      // Process this as offline time, batched
      this.processOfflineTime(deltaMilliseconds);
      // Don't also tick normally
      this.maybePersistTime();
      this.scheduleNextTick();
      return;
    }

    // Accumulate time for throttling
    this.accumulatedTime += deltaMilliseconds;

    // Only tick if we've accumulated enough time
    while (this.accumulatedTime >= this.tickInterval) {
      // Cap individual tick delta to prevent huge single ticks
      const tickDelta = Math.min(this.tickInterval, this.maxBatchTime);
      this.safeInvokeTick(tickDelta);
      this.accumulatedTime -= this.tickInterval;
    }

    // Maybe persist to localStorage (debounced)
    this.maybePersistTime();

    // Schedule next tick
    this.scheduleNextTick();
  }

  /**
   * Safely invoke the tick callback with error handling.
   *
   * @param deltaMilliseconds - Time since last tick
   */
  private safeInvokeTick(deltaMilliseconds: number): void {
    try {
      this.onTick(deltaMilliseconds);
    } catch (error) {
      // Log error but don't crash the loop
      console.error('[TickManager] Error in tick callback:', error);
      Sentry.captureException(error, {
        tags: { context: 'tick_manager' },
        extra: { deltaMilliseconds },
      });
    }
  }

  /**
   * Schedule the next tick based on visibility state.
   */
  private scheduleNextTick(): void {
    if (!this.isRunning) {
      return;
    }

    if (this.isPaused) {
      // When hidden, use setTimeout with longer interval (browsers throttle RAF)
      this.timeoutId = setTimeout(this.boundTick, this.tickInterval * 10);
    } else {
      // When visible, use requestAnimationFrame for smooth updates
      // Throttling is handled in tick() via accumulatedTime
      this.frameId = requestAnimationFrame(this.boundTick);
    }
  }

  /**
   * Cancel any scheduled tick.
   */
  private cancelScheduledTick(): void {
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Handle tab visibility changes.
   */
  private handleVisibilityChange(): void {
    const wasHidden = this.isPaused;
    this.isPaused = document.hidden;

    if (wasHidden && !this.isPaused && this.isRunning) {
      // Tab became visible - catch up on missed time
      const now = performance.now();
      const missedTime = now - this.lastTickTime;

      if (missedTime > this.tickInterval * 2) {
        // Significant time passed - process as offline time in batches
        this.processOfflineTime(missedTime);
        this.lastTickTime = now;
        this.accumulatedTime = 0;
      }

      // Switch from setTimeout back to RAF
      this.cancelScheduledTick();
      this.scheduleNextTick();
    } else if (!wasHidden && this.isPaused && this.isRunning) {
      // Tab became hidden - persist immediately and switch to setTimeout
      this.persistTime(true);
      this.cancelScheduledTick();
      this.scheduleNextTick();
    }
  }

  /**
   * Handle beforeunload event - persist time before page closes.
   */
  private handleBeforeUnload(): void {
    if (this.isRunning) {
      this.persistTime(true);
    }
  }

  /**
   * Persist last tick time to localStorage (debounced).
   */
  private maybePersistTime(): void {
    const now = Date.now();
    if (now - this.lastPersistTime >= this.persistInterval) {
      this.persistTime(false);
    }
  }

  /**
   * Actually persist the time to localStorage.
   *
   * @param force - Whether to persist regardless of debounce interval
   */
  private persistTime(force: boolean): void {
    const now = Date.now();
    if (force || now - this.lastPersistTime >= this.persistInterval) {
      if (this.currentCharacterId) {
        LocalStorageService.setLastTickTime(this.currentCharacterId, now);
      }
      this.lastPersistTime = now;
    }
  }
}
